import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Building2, TrendingUp, Shield, Clock, DollarSign, BarChart3, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TestimonialsCarousel from '../components/TestimonialsCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServicesCorporate() {
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoaded, setTestimonialsLoaded] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials/page/corporate`, {
        timeout: 10000 // 10 second timeout
      });
      if (response.data && Array.isArray(response.data)) {
        setTestimonials(response.data);
      } else {
        setTestimonials([]);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      setTestimonials([]);
    } finally {
      setTestimonialsLoaded(true);
    }
  };

  const benefits = [
    {
      icon: <DollarSign className="h-12 w-12 text-primary" />,
      title: "Better Returns",
      description: "Earn 6-8% returns on idle cash instead of 0% in current accounts. Every rupee works for your business."
    },
    {
      icon: <Clock className="h-12 w-12 text-secondary" />,
      title: "High Liquidity",
      description: "Access your funds within 1-2 business days when needed. Maintain operational flexibility."
    },
    {
      icon: <Shield className="h-12 w-12 text-primary" />,
      title: "Professional Management",
      description: "Expert fund managers handle your corporate treasury investments with proven debt strategies."
    },
    {
      icon: <BarChart3 className="h-12 w-12 text-secondary" />,
      title: "Tax Efficiency",
      description: "Indexation benefits on long-term capital gains reduce your tax liability significantly."
    }
  ];

  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: "#2C3540"}}>
      <section className="relative py-20" style={{backgroundColor: '#2C3540'}}>
        <div className="absolute inset-0 bg-background/90"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-6" data-testid="corporate-title">
              Services for <span style={{color: "#F5A623"}}>Corporate Clients</span>
            </h1>
            <p className="text-xl text-text-secondary">
              Optimize your corporate treasury management through strategic debt fund investments. Transform idle cash in current accounts into returns-generating assets while maintaining liquidity.
            </p>
          </div>
        </div>
      </section>

      <section style={{backgroundColor: "#1F2937"}} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl font-bold text-text-primary mb-4">The Corporate Cash Problem</h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Many businesses maintain large balances in current accounts for operational needs. While this provides liquidity, the opportunity cost is significant - especially in an inflationary environment where your money loses value every day.
            </p>
          </div>

          <div className="glass-effect p-10 md:p-16 rounded-2xl max-w-4xl mx-auto">
            <h3 className="font-heading text-3xl font-bold text-text-primary mb-8 text-center">Our Solution: Debt Funds</h3>
            <div className="space-y-6 text-lg text-text-secondary">
              <p>
                Debt funds invest in fixed-income securities and serve as an effective alternative to parking money in non-interest bearing current accounts. They offer:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-primary mr-3">•</span>
                  <span><strong className="text-text-primary">Liquid Funds:</strong> For 1 day to 90 days parking, earning 6-7% annually</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3">•</span>
                  <span><strong className="text-text-primary">Ultra Short Duration Funds:</strong> For 3-6 months needs, earning 7-8% annually</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3">•</span>
                  <span><strong className="text-text-primary">Short Duration Funds:</strong> For 6 months to 2 years, earning 7.5-8.5% annually</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" style={{backgroundColor: "#2C3540"}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl font-bold text-center text-text-primary mb-16">Why Choose Debt Funds?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="glass-effect p-8 rounded-2xl hover:-translate-y-1 transition-all" data-testid={`corporate-benefit-${index}`}>
                <div className="mb-4">{benefit.icon}</div>
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">{benefit.title}</h3>
                <p className="text-text-secondary">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{backgroundColor: "#1F2937"}} className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-effect p-10 md:p-16 rounded-2xl border border-primary/20">
            <h3 className="font-heading text-3xl font-bold text-text-primary mb-8">Example Scenario</h3>
            <div className="space-y-4 text-lg">
              <p className="text-text-secondary">Company XYZ maintains <span className="text-primary font-semibold">₹ 50 lakhs</span> in current account:</p>
              <ul className="space-y-3 text-text-secondary">
                <li className="flex items-start">
                  <span className="text-primary mr-3">•</span>
                  <span>Current earning: <span className="text-text-primary font-semibold">₹ 0 per year</span></span>
                </li>
                <li className="flex items-start">
                  <span className="text-secondary mr-3">•</span>
                  <span>Investment in liquid fund @ 7%: <span className="text-secondary font-semibold">₹ 3,50,000 per year</span></span>
                </li>
                <li className="flex items-start bg-primary/10 p-3 rounded-lg">
                  <span className="text-primary mr-3">•</span>
                  <span className="text-text-primary font-semibold">Opportunity cost of not investing: ₹ 3,50,000 annually</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsCarousel 
        testimonials={testimonials}
        isLoaded={testimonialsLoaded}
        title="What Our Corporate Clients Say"
        subtitle="Hear from businesses who've optimized their treasury management with us"
      />

      <section className="py-20" style={{backgroundColor: "#2C3540"}}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-text-primary mb-6">Make Your Business Money Work Harder</h2>
          <p className="text-xl text-text-secondary mb-8">Let's discuss how we can optimize your corporate treasury for better returns.</p>
          <Link to="/contact">
            <Button className="btn-primary text-lg px-10 py-6" data-testid="corporate-contact-button">
              Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
